import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { TaskItemView } from '../../api/generated/models/TaskItemView';
import { addNotification } from '../../store/slices/notificationSlice';
import { deleteTask } from '../../store/slices/taskSlice';
import AssigneeAvatars from './AssigneeAvatars';
import type { AppDispatch } from '../../store/store';
import {
  getTaskImageUrl,
  getTaskIssueLabel,
  getTaskShopUrl,
  TASK_MODAL_STATUSES,
} from '../../utils/taskHelpers';

interface TaskDetailModalProps {
  isOpen: boolean;
  task: TaskItemView | null;
  loading: boolean;
  updating: boolean;
  onClose: () => void;
  onStatusChange: (taskId: string, status: string) => void | Promise<void>;
  onEdit?: (task: TaskItemView) => void;
  /** False for subusers without mobile_write - edit/delete/status are blocked. */
  canWrite?: boolean;
}

const CopyIcon: React.FC = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </svg>
);

interface SuggestionItemProps {
  label: string;
  value: string;
  onCopy: (value: string) => void;
  copyLabel: string;
}

const SuggestionItem: React.FC<SuggestionItemProps> = ({
  label,
  value,
  onCopy,
  copyLabel,
}) => (
  <div className="tasks-detail-suggestion-item">
    <p>
      <strong>{label}:</strong> {value}
    </p>
    <button
      type="button"
      className="tasks-detail-suggestion-copy"
      onClick={() => onCopy(value)}
    >
      <CopyIcon />
      {copyLabel}
    </button>
  </div>
);

const TaskDetailModal: React.FC<TaskDetailModalProps> = ({
  isOpen,
  task,
  loading,
  updating,
  onClose,
  onStatusChange,
  onEdit,
  canWrite = true,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleCopySuggestion = useCallback(
    async (text: string) => {
      try {
        await navigator.clipboard.writeText(text);
        dispatch(
          addNotification({
            type: 'success',
            title: t('notification_success'),
            message: t('tasks_copy_success'),
          }),
        );
      } catch {
        dispatch(
          addNotification({
            type: 'error',
            title: t('notification_error'),
            message: t('tasks_copy_error'),
          }),
        );
      }
    },
    [dispatch, t],
  );

  useEffect(() => {
    if (!isOpen) {
      setShowSuggestions(false);
    }
  }, [isOpen]);

  useEffect(() => {
    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEsc);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen) {
    return null;
  }

  const listing = task?.listing ?? null;
  const imageUrl = getTaskImageUrl(listing?.images);
  const shopUrl = getTaskShopUrl(listing);
  const issues = listing?.issues ?? [];
  const suggestions = listing?.suggestions;
  const hasSuggestions = Boolean(
    suggestions?.title ||
    suggestions?.short_desc ||
    suggestions?.description ||
    suggestions?.keywords ||
    suggestions?.meta_desc,
  );

  const handleStatusChange = async (
    event: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    if (!task?.id) {
      return;
    }

    const nextStatus = event.target.value;
    if (nextStatus === task.status) {
      return;
    }

    try {
      await onStatusChange(task.id, nextStatus);
      onClose();
    } catch {
      // Keep modal open when update fails; error is handled in taskSlice.
    }
  };

  const handleDelete = async () => {
    if (!task?.id || deleting) {
      return;
    }
    if (!window.confirm(t('tasks_delete_confirm'))) {
      return;
    }

    setDeleting(true);
    try {
      await dispatch(deleteTask(task.id)).unwrap();
      onClose();
    } catch {
      // Keep modal open when delete fails; error is handled in taskSlice.
    } finally {
      setDeleting(false);
    }
  };

  const handleOverlayClick = (event: React.MouseEvent) => {
    if (event.target === event.currentTarget) {
      onClose();
    }
  };

  return (
    <div
      className="modal-overlay tasks-detail-overlay"
      onClick={handleOverlayClick}
    >
      <div
        className="tasks-detail-modal"
        onClick={(event) => event.stopPropagation()}
      >
        {loading || !task ? (
          <div className="tasks-detail-loading">
            <div className="loading-spinner" aria-label={t('loading_text')} />
          </div>
        ) : (
          <>
            <div className="tasks-detail-status-row">
              <label
                className="tasks-detail-status-label"
                htmlFor="task-status-select"
              >
                {t('tasks_modal_status_label')}
              </label>
              <div className="tasks-detail-status-select-wrap">
                <select
                  id="task-status-select"
                  className="tasks-detail-status-select"
                  value={task.status ?? TASK_MODAL_STATUSES[0]}
                  onChange={handleStatusChange}
                  disabled={updating || !canWrite}
                  title={!canWrite ? t('subuser_no_permission_tooltip') : undefined}
                >
                  {TASK_MODAL_STATUSES.map((status) => (
                    <option key={status} value={status}>
                      {t(`tasks_status_${status}`)}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {listing && (
              <>
                <div className="tasks-detail-image-wrap">
                  {imageUrl ? (
                    <img
                      src={imageUrl}
                      alt={task.title ?? t('tasks_untitled')}
                      className="tasks-detail-image"
                    />
                  ) : (
                    <div
                      className="tasks-detail-image-placeholder"
                      aria-hidden="true"
                    />
                  )}
                </div>

                <div className="tasks-detail-badges">
                  {listing.sku && (
                    <span className="tasks-detail-badge tasks-detail-badge--sku">
                      {t('tasks_modal_sku', { sku: listing.sku })}
                    </span>
                  )}
                  <span className="tasks-detail-badge tasks-detail-badge--issues">
                    {t('tasks_modal_issues_count', { count: issues.length })}
                  </span>
                </div>
              </>
            )}

            <h2 className="tasks-detail-title">
              {task.title || t('tasks_untitled')}
            </h2>

            {task.description && (
              <p className="tasks-detail-description">{task.description}</p>
            )}

            <div className="tasks-detail-assignees">
              <span className="tasks-detail-assignees-label">{t('tasks_assignees')}</span>
              <AssigneeAvatars
                size="md"
                assignees={(task.assignees ?? [])
                  .filter((row): row is typeof row & { id: string } => Boolean(row.id))
                  .map((row) => ({
                    id: row.id,
                    name: row.name,
                    initials: row.initials,
                  }))}
              />
            </div>

            {listing && (
              <div className="tasks-detail-review">
                <div className="tasks-detail-review-header">
                  <span className="tasks-detail-review-label">
                    {t('tasks_modal_quality_review')}
                  </span>
                  {hasSuggestions && (
                    <button
                      type="button"
                      className="tasks-detail-suggestions-link"
                      onClick={() => setShowSuggestions((value) => !value)}
                    >
                      {showSuggestions
                        ? t('tasks_modal_hide_suggestions')
                        : t('tasks_modal_see_suggestions')}
                    </button>
                  )}
                </div>

                {issues.length > 0 && (
                  <div className="tasks-detail-issues">
                    {issues.map((issue) => (
                      <span key={issue} className="tasks-detail-issue-tag">
                        {getTaskIssueLabel(issue, t)}
                      </span>
                    ))}
                  </div>
                )}

                {showSuggestions && hasSuggestions && suggestions && (
                  <div className="tasks-detail-suggestions">
                    {suggestions.title && (
                      <SuggestionItem
                        label={t('tasks_suggestion_title')}
                        value={suggestions.title}
                        onCopy={handleCopySuggestion}
                        copyLabel={t('tasks_suggestion_copy')}
                      />
                    )}
                    {suggestions.short_desc && (
                      <SuggestionItem
                        label={t('tasks_suggestion_short_desc')}
                        value={suggestions.short_desc}
                        onCopy={handleCopySuggestion}
                        copyLabel={t('tasks_suggestion_copy')}
                      />
                    )}
                    {suggestions.description && (
                      <SuggestionItem
                        label={t('tasks_suggestion_description')}
                        value={suggestions.description}
                        onCopy={handleCopySuggestion}
                        copyLabel={t('tasks_suggestion_copy')}
                      />
                    )}
                    {suggestions.keywords && (
                      <SuggestionItem
                        label={t('tasks_suggestion_keywords')}
                        value={suggestions.keywords}
                        onCopy={handleCopySuggestion}
                        copyLabel={t('tasks_suggestion_copy')}
                      />
                    )}
                    {suggestions.meta_desc && (
                      <SuggestionItem
                        label={t('tasks_suggestion_meta_desc')}
                        value={suggestions.meta_desc}
                        onCopy={handleCopySuggestion}
                        copyLabel={t('tasks_suggestion_copy')}
                      />
                    )}
                  </div>
                )}
              </div>
            )}

            <div className="tasks-detail-actions">
              <button
                type="button"
                className="btn btn-secondary tasks-detail-cancel-btn"
                onClick={onClose}
              >
                {t('close')}
              </button>
              {onEdit && task.source === TaskItemView.source.CUSTOM && (
                <button
                  type="button"
                  className="btn btn-secondary tasks-detail-edit-btn"
                  onClick={() => onEdit(task)}
                  disabled={deleting || !canWrite}
                  title={!canWrite ? t('subuser_no_permission_tooltip') : undefined}
                >
                  {t('tasks_edit')}
                </button>
              )}
              <button
                type="button"
                className="btn btn-danger tasks-detail-delete-btn"
                onClick={handleDelete}
                disabled={deleting || !canWrite}
                title={!canWrite ? t('subuser_no_permission_tooltip') : undefined}
              >
                {t('tasks_delete')}
              </button>
              {shopUrl && (
                <a
                  href={shopUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-primary tasks-detail-shop-btn"
                >
                  {t('tasks_modal_visit_shop')}
                  <span className="tasks-detail-shop-arrow" aria-hidden="true">
                    →
                  </span>
                </a>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default TaskDetailModal;
